import React, { useState } from 'react';
import { login } from '@obis/auth';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      // Fresh live ID token for the immediate log-login call; the live token is
      // sourced per-request via the shared API client elsewhere.
      const token = await user.user.getIdToken();
      console.log('✅ Login successful');
      
      // Log the login activity
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
        await fetch(`${API_URL}/auth/log-login`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (logError) {
        console.error('Failed to log login activity:', logError);
      }
      navigate('/app/default-tenant');
    } catch (err: any) {
      setError(err.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const fillTestCredentials = (role: 'admin' | 'counselor') => {
    if (role === 'admin') {
      setEmail('admin@olivebloom.edu');
      setPassword('Admin@123456');
    } else {
      setEmail('counselor@olivebloom.edu');
      setPassword('Counselor@123');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="glass-card max-w-md w-full p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gradient mb-2">
            OBIS CRM System
          </h1>
          <p className="text-gray-600">Olive Bloom International School</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="your.email@olivebloom.edu"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-3 text-center">Test Credentials:</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fillTestCredentials('admin')}
              className="btn-outline flex-1 text-sm py-2"
            >
              Admin
            </button>
            <button
              type="button"
              onClick={() => fillTestCredentials('counselor')}
              className="btn-outline flex-1 text-sm py-2"
            >
              Counselor
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
